import { Link } from "react-router-dom";
import { coverUrl } from "@/lib/api";
import { STORE, PRODUCT } from "@/constants/testIds";
import Countdown from "./Countdown";
import VerifiedBadge from "./VerifiedBadge";
import ShareButton from "./ShareButton";
import { Star } from "lucide-react";

const CATEGORY_LABEL = {
  ebook: "E-BOOK",
  podcast: "PODCAST",
  music: "MUSIC",
  image: "IMAGE",
  video: "VIDEO",
  bundle: "BUNDLE",
};

export default function ProductCard({ product }) {
  const soldOut = product.sold_count >= product.total_stock;
  const remaining = product.total_stock - product.sold_count;
  const pctSold = product.total_stock > 0 ? product.sold_count / product.total_stock : 0;
  const sellingFast = !soldOut && (pctSold >= 0.8 || (product.sales_24h && product.sales_24h >= 3));
  return (
    <Link
      to={`/p/${product.id}`}
      data-testid={STORE.productCard(product.id)}
      className="drop-card group block bg-[#0A0A0A] border border-white/10 hover:border-white/30 relative overflow-hidden"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[#141414]">
        <img
          src={coverUrl(product.cover_storage_path)}
          alt={product.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent pointer-events-none" />
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap max-w-[80%]">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] bg-black/70 text-[#CCFF00] border border-[#CCFF00]/40 px-2 py-1">
            {CATEGORY_LABEL[product.category] || product.category}
          </span>
          {product.creator_verified && <VerifiedBadge testId={`card-verified-${product.id}`} />}
          {soldOut && (
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] bg-[#FF3366] text-black px-2 py-1">
              SOLD OUT
            </span>
          )}
          {sellingFast && (
            <span
              data-testid={`card-selling-fast-${product.id}`}
              className="font-mono text-[10px] uppercase tracking-[0.2em] bg-[#FF3366] text-white border border-[#FF3366] px-2 py-1 urgent-pulse"
            >
              SELLING FAST
            </span>
          )}
        </div>
        {product.drop_ends_at && (
          <div className="absolute top-3 right-3">
            <Countdown endsAt={product.drop_ends_at} />
          </div>
        )}
        <div
          className={`absolute right-3 z-20 ${product.drop_ends_at ? "top-14" : "top-3"}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <ShareButton
            testId={`card-share-${product.id}`}
            variant="card"
            stopPropagation
            url={typeof window !== "undefined" ? `${window.location.origin}/p/${product.id}` : `/p/${product.id}`}
            shareUrl={typeof window !== "undefined" ? `${window.location.origin}/api/share/product/${product.id}` : `/api/share/product/${product.id}`}
            title={`${product.title} · by ${product.creator_name}`}
            description={product.title}
          />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 truncate">
                {product.creator_name}
              </div>
              <div className="font-display font-bold text-xl leading-tight text-white line-clamp-2">
                {product.title}
              </div>
              {product.rating_count > 0 && (
                <div className="mt-1.5 inline-flex items-center gap-1 font-mono text-[10px] text-white/70">
                  <Star className="w-3 h-3 fill-[#CCFF00] text-[#CCFF00]" />
                  {product.rating_avg.toFixed(1)} ({product.rating_count})
                </div>
              )}
            </div>
            <div className="text-right shrink-0">
              <div data-testid={PRODUCT.price} className="font-mono text-lg font-bold text-[#CCFF00]">
                ${product.price.toFixed(2)}
              </div>
              <div data-testid={PRODUCT.stockCounter} className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                {product.sold_count}/{product.total_stock} · {remaining} LEFT
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
